/**
 * Action Tool Handlers
 * These are separate from the read-only tool handlers because they modify data
 * and require user confirmation before execution.
 */

import { recordsService } from '../services/recordsService';
import { transactionsService } from '../services/transactionsService';
import { transactionEntitiesService } from '../services/transactionEntitiesService';
import { apiClient } from '../utils/api';
import { UrlBuilder } from '../utils/urls';
import type { ToolResult } from './toolHandlers';
import type { useAppContext } from '../contexts/Storage';

/**
 * Parse the arguments from a tool call
 */
function parseArgs(args: string): Record<string, unknown> {
  try {
    return JSON.parse(args);
  } catch {
    return {};
  }
}

/**
 * Create all action tool handlers.
 * These are separated from read tools for security (they require confirmation).
 */
export function createActionHandlers(appContext: ReturnType<typeof useAppContext>) {
  const { token, user, selectedRecord } = appContext;

  if (!token) {
    throw new Error('Authentication token not available');
  }

  const userId = user?.id;
  if (!userId) {
    throw new Error('User not identified');
  }

  return {

    // ============ RECORD ACTIONS ============

    create_record: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const data: any = {
          record_name: params.record_name as string,
          record_creator_id: userId,
        };
        // Only include description if provided and not empty
        if (params.record_description && typeof params.record_description === 'string' && params.record_description.trim()) {
          data.record_description = params.record_description;
        }
        if (params.record_currency_code) {
          data.record_currency_code = params.record_currency_code as string;
        }
        const result = await recordsService.createRecord(token, data);
        if (!result.success) throw new Error(result.error || 'Failed to create record');
        return {
          success: true,
          data: result.data,
          formattedMessage: `Record "${params.record_name}" created successfully.`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    update_record: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const data: any = { user_id: userId };
        if (params.record_name) data.record_name = params.record_name;
        // Handle description: keep unchanged if not provided
        if (params.record_description !== undefined) {
          data.record_description = params.record_description;
        }
        if (params.record_currency_code) data.record_currency_code = params.record_currency_code;
        const result = await recordsService.updateRecord(token, Number(params.record_id), data);
        if (!result.success) throw new Error(result.error || 'Failed to update record');
        return {
          success: true,
          data: result.data,
          formattedMessage: `Record updated successfully.`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    delete_record: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const result = await recordsService.deleteRecord(
          token,
          Number(params.record_id),
          Number(params.creator_id),
          params.verification_code as string
        );
        if (!result.success) throw new Error(result.error || 'Failed to delete record');
        return {
          success: true,
          data: result.data,
          formattedMessage: `Record deleted successfully.`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    // ============ TRANSACTION ACTIONS ============

    create_transaction: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const data: any = {
          transaction_record_id: Number(params.record_id),
          transaction_date: params.transaction_date as string,
          transaction_action_id: Number(params.transaction_action_id),
          transaction_object_id: Number(params.transaction_object_id),
          transaction_media_id: Number(params.transaction_media_id),
          transaction_quantity: Number(params.transaction_quantity),
          transaction_total_amount: Number(params.transaction_total_amount),
        };
        if (params.transaction_indirect_object_id) {
          data.transaction_indirect_object_id = Number(params.transaction_indirect_object_id);
        }
        const result = await transactionsService.createTransaction(token, userId, data);
        if (!result.success) throw new Error(result.error || 'Failed to create transaction');
        return {
          success: true,
          data: result.data,
          formattedMessage: `Transaction created successfully.`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    update_transaction: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const data: any = {
          transaction_record_id: Number(params.record_id),
          transaction_action_id: Number(params.transaction_action_id),
          transaction_object_id: Number(params.transaction_object_id),
          transaction_media_id: Number(params.transaction_media_id),
          transaction_quantity: Number(params.transaction_quantity),
          transaction_total_amount: Number(params.transaction_total_amount),
        };
        if (params.transaction_date) {
          data.transaction_date = params.transaction_date as string;
        }
        if (params.transaction_indirect_object_id) {
          data.transaction_indirect_object_id = Number(params.transaction_indirect_object_id);
        }
        const result = await transactionsService.updateTransaction(token, Number(params.transaction_id), userId, data);
        if (!result.success) throw new Error(result.error || 'Failed to update transaction');
        return {
          success: true,
          data: result.data,
          formattedMessage: `Transaction updated successfully.`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    delete_transaction: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const result = await transactionsService.deleteTransaction(token, Number(params.transaction_id));
        if (!result.success) throw new Error(result.error || 'Failed to delete transaction');
        return {
          success: true,
          data: result.data,
          formattedMessage: `Transaction deleted successfully.`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    // ============ ENTITY ACTIONS ============

    create_entity: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const data: any = {
          transaction_entity_name: params.transaction_entity_name as string,
          transaction_entity_category: params.transaction_entity_category as string,
        };
        if (params.transaction_entity_description) data.transaction_entity_description = params.transaction_entity_description;
        if (params.transaction_entity_type) data.transaction_entity_type = params.transaction_entity_type;
        if (params.transaction_entity_past_tense) data.transaction_entity_past_tense = params.transaction_entity_past_tense;
        const result = await transactionEntitiesService.createTransactionEntity(token, userId, data);
        if (!result.success) throw new Error(result.error || 'Failed to create entity');
        return {
          success: true,
          data: result.data,
          formattedMessage: `Entity "${params.transaction_entity_name}" created successfully.`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    update_entity: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const recordId = params.record_id != null ? Number(params.record_id) : selectedRecord?.id;
        if (recordId == null) {
          throw new Error('record_id is required. Select books first, then retry.');
        }
        const data: any = { record_id: recordId };
        if (params.transaction_entity_name) data.transaction_entity_name = params.transaction_entity_name;
        if (params.transaction_entity_description) data.transaction_entity_description = params.transaction_entity_description;
        if (params.transaction_entity_category) data.transaction_entity_category = params.transaction_entity_category;
        if (params.transaction_entity_type) data.transaction_entity_type = params.transaction_entity_type;
        if (params.transaction_entity_past_tense) data.transaction_entity_past_tense = params.transaction_entity_past_tense;
        const result = await transactionEntitiesService.updateTransactionEntity(token, Number(params.entity_id), data);
        if (!result.success) throw new Error(result.error || 'Failed to update entity');
        return {
          success: true,
          data: result.data,
          formattedMessage: `Entity updated successfully.`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    delete_entity: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const result = await transactionEntitiesService.deleteTransactionEntity(token, Number(params.entity_id));
        if (!result.success) throw new Error(result.error || 'Failed to delete entity');
        return {
          success: true,
          data: result.data,
          formattedMessage: `Entity deleted successfully.`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    // ============ RECORD PERMISSION ACTIONS ============

    join_record: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const result = await recordsService.joinRecordByCode(token, params.code as string, userId);
        if (!result.success) throw new Error(result.error || 'Failed to join record');
        return {
          success: true,
          data: result.data,
          formattedMessage: `Successfully joined record via invite code.`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    grant_permission: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const data = {
          record_id: Number(params.record_id),
          target_user_id: Number(params.target_user_id),
          can_read: Boolean(params.can_read),
          can_write: Boolean(params.can_write),
        };
        const result = await recordsService.grantPermission(token, userId, data);
        if (!result.success) throw new Error(result.error || 'Failed to grant permission');
        return {
          success: true,
          data: result.data,
          formattedMessage: `Permissions updated successfully.`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    // ============ PROFILE & SETTINGS ACTIONS ============

    go_to_profile: async (): Promise<ToolResult> => {
      return {
        success: true,
        data: null,
        formattedMessage: `Navigating to your profile page. You can update your name, email, password, and profile picture there.`,
      };
    },

    update_profile: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const data: any = {};
        if (params.first_name) data.firstname = params.first_name;
        if (params.last_name) data.lastname = params.last_name;
        if (params.username) data.username = params.username;
        if (Object.keys(data).length === 0) {
          return { success: false, data: null, error: 'No profile fields provided to update.' };
        }
        const response = await apiClient.putWithToken(
          UrlBuilder.user.update(),
          token,
          data
        );
        if (!response.success) throw new Error(response.error || 'Failed to update profile');
        return {
          success: true,
          data: response.data,
          formattedMessage: `Profile updated successfully.`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    toggle_dark_mode: async (): Promise<ToolResult> => {
      return {
        success: true,
        data: null,
        formattedMessage: `Use the toggle switch in the sidebar to switch between light and dark mode. You can also navigate to your Profile settings page.`,
      };
    },

    download_apk: async (): Promise<ToolResult> => {
      const baseUrl = process.env.REACT_APP_API_URL || '';
      return {
        success: true,
        data: null,
        formattedMessage: `You can download the Balancio Pro APK by clicking the "Download APK" button in the sidebar, or visit: ${baseUrl}download/apk`,
      };
    },
  };
}
